import { Close, Info, Lock, ReportProblem } from "@mui/icons-material";
import {
  Box,
  Chip,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import CustomSwitch from "../../FormFields/CustomSwitch";
import { formatDateTime } from "../../../base/FormatTime";

const statusColors = {
  reported: { bg: "#EFF8FF", color: "#175CD3", border: "#B2DDFF" },
  under_investigation: { bg: "#FFFAEB", color: "#B54708", border: "#FEDF89" },
  review_required: { bg: "#FEF3F2", color: "#B42318", border: "#FECDCA" },
  closed: { bg: "#ECFDF3", color: "#027A48", border: "#ABEFC6" },
};

const IncidentDialogueTitle = ({
  formData,
  handleFormChange,
  handleClose,
}) => {
  const theme = useTheme();
  const c =
    statusColors[formData.status] || {
      bg: "#F2F4F7",
      color: "#475467",
      border: "#EAECF0",
    };

  return (
    <DialogTitle
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2.5,
        pb: 2,
        pr: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "8px",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <ReportProblem
              sx={{ fontSize: 20, color: theme.palette.primary.main }}
            />
          </Box>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography
                variant="h6"
                fontWeight={700}
                color="#1A1A2E"
                fontSize="1rem"
              >
                {formData.incident_type_display ||
                  formData.incident_type ||
                  "Incident"}
              </Typography>
              <Chip
                label={formData.status_display || "—"}
                size="small"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.68rem",
                  height: 20,
                  bgcolor: c.bg,
                  color: c.color,
                  border: `1px solid ${c.border}`,
                }}
              />
            </Box>
            <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
              Ref no. {formData.reference_number || "—"}
            </Typography>
          </Box>
        </Box>

        <IconButton
          onClick={handleClose}
          size="small"
          sx={{
            width: 32,
            height: 32,
            color: "#6B7280",
            "&:hover": { bgcolor: "#F2F4F7" },
          }}
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
          p: 1.5,
          borderRadius: "10px",
          bgcolor: "#F9FAFB",
          border: "1px solid #EAECF0",
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="#6B7280"
            fontSize="0.68rem"
            fontWeight={600}
            sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}
          >
            Date & Time of Incident
          </Typography>
          <Typography
            variant="body2"
            fontSize="0.8rem"
            fontWeight={600}
            color="#1A1A2E"
          >
            {formatDateTime(formData.created_at) || "—"}
          </Typography>
        </Box>

        <Box sx={{ textAlign: { xs: "left", sm: "right" } }}>
          <Typography
            variant="caption"
            color="#6B7280"
            fontSize="0.68rem"
            fontWeight={600}
            sx={{ textTransform: "uppercase", letterSpacing: "0.3px" }}
          >
            Date & Time Reported
          </Typography>
          <Typography
            variant="body2"
            fontSize="0.8rem"
            fontWeight={600}
            color="#1A1A2E"
          >
            {formatDateTime(formData.updated_at) || "—"}
          </Typography>
          <Typography variant="caption" color="#6B7280" fontSize="0.7rem">
            By {formData.reporter || formData.reported_by_detail?.name || "—"}
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          border: "1px solid #EAECF0",
          borderRadius: "10px",
          px: 1.5,
          py: 1,
          bgcolor: "#FAFBFC",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Lock sx={{ fontSize: 16, color: "#475467" }} />
            <Typography
              variant="body2"
              fontSize="0.8rem"
              fontWeight={600}
              color="#1A1A2E"
            >
              Keep Private
            </Typography>
          </Box>
          <CustomSwitch
            name="keepPrivate"
            checked={formData.keepPrivate}
            onChange={handleFormChange}
            label={formData.keepPrivate ? "YES" : "NO"}
          />
        </Box>

        <Tooltip title="When enabled, this incident will be hidden from handover notes and general visibility.">
          <IconButton
            size="small"
            sx={{
              color: "#94A3B8",
              "&:hover": { color: "#475467", bgcolor: "#F2F4F7" },
            }}
          >
            <Info sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>
    </DialogTitle>
  );
};

export default IncidentDialogueTitle;